import { useActionSheet } from '@expo/react-native-action-sheet';
import * as Clipboard from 'expo-clipboard';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { userInfoAtom } from 'app/atoms';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import ImageViewerModal from 'app/design/primitives/image-viewer-modal';
import { Text } from 'app/design/primitives/text';
import { Thumbnail } from 'app/design/primitives/thumbnail';
import { RequestImage } from 'app/components/request-image';
import { PostsFeedFooter } from 'app/components/posts-feed-footer';

import { localeAwareFormatDistance } from 'app/lib/date';
import { SUPPORT_CHANNEL_URL } from 'app/network/chat/channel';
import { useLikePost, useUnlikePost } from 'app/network/post';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import type { PostResponse } from 'app/types/post';
import type { SpecificNavigationProp } from 'app/types/navigation';
import { ListingType } from 'app/types/listing';
import { isMobileNative, useUISize } from 'app/utils/web-helpers';
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Share from 'react-native-share';
import Toast2 from 'react-native-root-toast';
import {
  AppState,
  FlatList,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Pressable,
  type StyleProp,
  StyleSheet,
  type TextLayoutEvent,
  View,
  type ViewStyle,
  type ViewToken,
} from 'react-native';
import Svg, { Defs, LinearGradient, Path, Rect, Stop } from 'react-native-svg';
import { useAtomValue } from 'jotai';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconDots } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { navigateToListing, navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import Reanimated, {
  cancelAnimation,
  type ScrollHandlerProcessed,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
const theme = createDSTheme();
const POST_CAPTION_MAX_LINES = 3;

function ItemSeparator() {
  return <View style={{ height: theme.spacing['spacing.general.x2'] }} />;
}

interface PostCardProps {
  post: PostResponse;
  showListingAvatar: boolean;
}

function HeartOutlineIcon({ color, size = 20 }: { color: string; size?: number }) {
  return (
    <Svg height={size} viewBox="0 0 24 24" width={size}>
      <Path
        d="m20.84 4.61-1.54-1.35A5.5 5.5 0 0 0 12 4.09a5.5 5.5 0 0 0-7.3-.83L3.16 4.61a5.5 5.5 0 0 0-.41 7.78l7.73 7.11a2 2 0 0 0 2.72 0l7.73-7.11a5.5 5.5 0 0 0-.09-7.78Z"
        fill="none"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
      />
    </Svg>
  );
}

function HeartFilledIcon({ color, size = 20 }: { color: string; size?: number }) {
  return (
    <Svg height={size} viewBox="0 0 24 24" width={size}>
      <Path
        d="m20.84 4.61-1.54-1.35A5.5 5.5 0 0 0 12 4.09a5.5 5.5 0 0 0-7.3-.83L3.16 4.61a5.5 5.5 0 0 0-.41 7.78l7.73 7.11a2 2 0 0 0 2.72 0l7.73-7.11a5.5 5.5 0 0 0-.09-7.78Z"
        fill={color}
      />
    </Svg>
  );
}

function CommentOutlineIcon({ color, size = 20 }: { color: string; size?: number }) {
  return (
    <Svg height={size} viewBox="0 0 24 24" width={size}>
      <Path
        d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5A8.48 8.48 0 0 1 21 11v.5Z"
        fill="none"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
      />
    </Svg>
  );
}

function MediaSlide({
  picture,
  listingName,
  isLargeUI,
}: {
  isLargeUI: boolean;
  picture: PostResponse['pictures'][number];
  listingName: string;
}) {
  return (
    <View style={[styles.mediaFrame, isLargeUI && styles.mediaFrameLarge]}>
      {picture.url ? (
        <PCImage contentFit="cover" source={{ uri: picture.url }} style={styles.mediaImage} transition={180} />
      ) : (
        <View style={styles.mediaFallbackBackground}>
          <Text color="surface.default" fontWeight="700" style={styles.mediaFallbackTitle}>
            {listingName}
          </Text>
        </View>
      )}
    </View>
  );
}

function getPostCardDetails(post: PostResponse, deletedUserLabel: string) {
  const listing = post.loan.listing;
  const lender = post.loan.lender;

  return {
    authorName: post.user.profile?.firstName ?? '-',
    commentsCount: post.commentsCount,
    imageUrls: post.pictures.map((picture) => ({ url: picture.url })),
    lender,
    lenderName: lender?.profile?.firstName ?? deletedUserLabel,
    listingId: listing.id,
    listingName: listing.item.name,
    listingPicture: listing.picture ?? '',
    listingType: listing.type,
    pictures: post.pictures,
    shareMessage: [post.user.profile?.firstName ?? '-', post.text, post.pictures[0]?.url].filter(Boolean).join('\n\n'),
    usesRequestIcon: listing.type === ListingType.request || !listing.picture,
  };
}

function usePostLike(post: PostResponse) {
  const likeScale = useSharedValue(1);
  const likeScaleStyle = useAnimatedStyle(() => ({ transform: [{ scale: likeScale.get() }] }));
  const [optimisticLike, setOptimisticLike] = useState({ source: post.isLiked, value: post.isLiked });
  let currentOptimisticLike = optimisticLike;

  if (optimisticLike.source !== post.isLiked) {
    currentOptimisticLike = { source: post.isLiked, value: post.isLiked };
    setOptimisticLike(currentOptimisticLike);
  }

  const likePost = useLikePost();
  const unlikePost = useUnlikePost();
  const isLiked = currentOptimisticLike.value;
  const isUpdatingLike = likePost.isPending || unlikePost.isPending;
  const likesCount = post.likesCount + Number(isLiked) - Number(post.isLiked);

  const toggleLike = () => {
    if (isUpdatingLike) return;
    const next = !isLiked;

    setOptimisticLike({ source: post.isLiked, value: next });

    if (next) {
      cancelAnimation(likeScale);
      likeScale.set(0.92);
      likeScale.set(
        withSequence(
          withTiming(1.14, { duration: 120 }),
          withSpring(1, { damping: 30.48, mass: 1, stiffness: 469.55 }),
        ),
      );
    }

    const mutation = next ? likePost : unlikePost;
    mutation.mutate(post.id, {
      onError: () => setOptimisticLike({ source: post.isLiked, value: post.isLiked }),
    });
  };

  return { isLiked, isUpdatingLike, likeScaleStyle, likesCount, toggleLike };
}

function PostCardHeader({
  authorName,
  overlay = false,
  navigation,
  onOpenActionsMenu,
  post,
  timestamp,
}: {
  authorName: string;
  overlay?: boolean;
  navigation: SpecificNavigationProp;
  onOpenActionsMenu: () => void;
  post: PostResponse;
  timestamp: string;
}) {
  const { t } = useTranslation('posts');

  return (
    <Row alignItems="center" style={[styles.header, !overlay && styles.textOnlyHeader]}>
      <Pressable
        accessibilityRole="button"
        onPress={() => navigateToUserProfile(navigation, post.user.id)}
        style={{ flexDirection: 'row', alignItems: 'center', flex: 1, gap: theme.spacing['spacing.general.x2'] }}
      >
        <View style={overlay ? styles.authorAvatarRing : undefined}>
          <Thumbnail height={40} source={{ uri: post.user.profile?.picture ?? undefined }} />
        </View>
        <Column flex={1}>
          <Text fontWeight="700" numberOfLines={1} style={[styles.authorName, overlay && styles.overlayAuthorName]}>
            {authorName}
          </Text>
          <Text numberOfLines={1} style={[styles.timestamp, overlay && styles.overlayTimestamp]}>
            {timestamp}
          </Text>
        </Column>
      </Pressable>

      <Row alignItems="center" gap="spacing.general.x1">
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('feed.postOptions')}
          hitSlop={6}
          onPress={onOpenActionsMenu}
          style={[styles.menuButton, overlay && styles.overlayMenuButton]}
        >
          <IconDots
            strokeWidth={ICON_STROKE_WIDTH}
            size={25}
            color={theme.colors[overlay ? 'icon.inverse.primary' : 'text.secondary']}
          />
        </Pressable>
      </Row>
    </Row>
  );
}

function PostListingContext({
  lender,
  lenderLabel,
  lenderName,
  listingId,
  listingLabel,
  listingName,
  listingPicture,
  listingType,
  navigation,
  showListingAvatar,
  usesRequestIcon,
}: {
  lender: PostResponse['loan']['lender'];
  lenderLabel: string;
  lenderName: string;
  listingId: string;
  listingLabel: string;
  listingName: string;
  listingPicture: string;
  listingType: ListingType;
  navigation: SpecificNavigationProp;
  showListingAvatar: boolean;
  usesRequestIcon: boolean;
}) {
  if (!showListingAvatar) return null;

  return (
    <Row alignItems="center" gap="spacing.general.x2" style={styles.listingContext}>
      <Pressable
        accessibilityRole="button"
        hitSlop={{ top: 4, bottom: 4 }}
        accessibilityLabel={`${listingLabel}: ${listingName}`}
        onPress={() => navigateToListing(navigation, listingId)}
        style={({ pressed }) => [styles.listingContextItem, pressed && styles.contextPressed]}
      >
        <View style={styles.listingContextThumbnail}>
          {usesRequestIcon ? (
            <RequestImage
              fontSize={15}
              isFound
              style={{ height: '100%', width: '100%', borderRadius: theme.borderRadii['radius.full'] }}
            />
          ) : (
            <PCImage contentFit="cover" source={{ uri: listingPicture }} style={styles.listingContextImage} />
          )}
        </View>
        <View style={styles.contextCopy}>
          <Text numberOfLines={1} style={styles.contextLabel}>
            {listingLabel}
          </Text>
          <View style={styles.contextNameRow}>
            <Text fontWeight="500" numberOfLines={1} style={styles.listingContextText}>
              {listingName}
            </Text>
          </View>
        </View>
      </Pressable>
      {lender ? (
        <Pressable
          accessibilityRole="button"
          hitSlop={{ top: 4, bottom: 4 }}
          accessibilityLabel={`${lenderLabel}: ${lenderName}`}
          onPress={() => navigateToUserProfile(navigation, lender.id, listingId, listingType)}
          style={({ pressed }) => [styles.lenderContextItem, pressed && styles.contextPressed]}
        >
          <Thumbnail height={24} source={{ uri: lender.profile?.picture ?? undefined }} />
          <View style={styles.contextCopy}>
            <Text numberOfLines={1} style={styles.contextLabel}>
              {lenderLabel}
            </Text>
            <View style={styles.contextNameRow}>
              <Text fontWeight="500" numberOfLines={1} style={styles.lenderContextText}>
                {lenderName}
              </Text>
            </View>
          </View>
        </Pressable>
      ) : null}
    </Row>
  );
}

function PostImageScrim() {
  const gradientId = `post-scrim-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  return (
    <View pointerEvents="none" style={styles.headerGradient}>
      <Svg height="100%" width="100%" viewBox="0 0 1 1" preserveAspectRatio="none">
        <Defs>
          <LinearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0" stopColor="#000" stopOpacity={0.68} />
            <Stop offset="0.2" stopColor="#000" stopOpacity={0.48} />
            <Stop offset="0.45" stopColor="#000" stopOpacity={0.22} />
            <Stop offset="0.7" stopColor="#000" stopOpacity={0.06} />
            <Stop offset="0.9" stopColor="#000" stopOpacity={0} />
            <Stop offset="1" stopColor="#000" stopOpacity={0} />
          </LinearGradient>
        </Defs>
        <Rect width="1" height="1" fill={`url(#${gradientId})`} />
      </Svg>
    </View>
  );
}

function PostMedia({
  header,
  currentMediaWidth,
  isLargeUI,
  listingName,
  onImagePress,
  onMediaLayout,
  onMediaScrollEnd,
  pictures,
  visibleImageIndex,
}: {
  header: React.ReactNode;
  currentMediaWidth: number;
  isLargeUI: boolean;
  listingName: string;
  onImagePress: (index: number) => void;
  onMediaLayout: (event: { nativeEvent: { layout: { width: number } } }) => void;
  onMediaScrollEnd: (event: { nativeEvent: { contentOffset: { x: number } } }) => void;
  pictures: PostResponse['pictures'];
  visibleImageIndex: number;
}) {
  const { t } = useTranslation('posts');

  if (pictures.length === 0) return null;

  const pictureCount = pictures.length;
  const activeIndex = Math.max(0, Math.min(visibleImageIndex, pictureCount - 1));
  const firstDotIndex = Math.min(Math.max(activeIndex - 2, 0), Math.max(pictureCount - 5, 0));
  const singlePicture = pictures.length === 1 ? pictures[0] : undefined;
  const mediaContent = singlePicture ? (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={t('feed.openPhoto', { current: 1, total: pictures.length })}
      onPress={() => onImagePress(0)}
    >
      <MediaSlide isLargeUI={isLargeUI} listingName={listingName} picture={singlePicture} />
    </Pressable>
  ) : (
    <FlatList
      key={currentMediaWidth}
      data={pictures}
      getItemLayout={(_, index) => ({ length: currentMediaWidth, offset: currentMediaWidth * index, index })}
      initialScrollIndex={activeIndex}
      decelerationRate="fast"
      horizontal
      keyExtractor={(item) => item.id}
      onMomentumScrollEnd={onMediaScrollEnd}
      pagingEnabled
      renderItem={({ item, index }) => (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('feed.openPhoto', { current: index + 1, total: pictures.length })}
          onPress={() => onImagePress(index)}
          style={{ width: currentMediaWidth }}
        >
          <MediaSlide isLargeUI={isLargeUI} listingName={listingName} picture={item} />
        </Pressable>
      )}
      showsHorizontalScrollIndicator={false}
    />
  );

  return (
    <View onLayout={onMediaLayout} style={styles.mediaSection}>
      {mediaContent}
      <PostImageScrim />
      <View pointerEvents="box-none" style={styles.mediaHeader}>
        {header}
      </View>
      {pictureCount > 1 ? (
        <View
          pointerEvents="none"
          accessible
          accessibilityLabel={t('feed.photoPosition', { current: activeIndex + 1, total: pictureCount })}
          style={styles.carouselPosition}
        >
          <View style={styles.pagination}>
            {pictures.slice(firstDotIndex, firstDotIndex + 5).map((picture, index) => (
              <View
                key={picture.id}
                style={[styles.paginationDot, index + firstDotIndex === activeIndex && styles.paginationDotActive]}
              />
            ))}
          </View>
        </View>
      ) : null}
    </View>
  );
}

function PostCaption({
  hasPictures,
  expanded,
  isCaptionTruncated,
  onTextLayout,
  onToggle,
  readLessLabel,
  readMoreLabel,
  text,
}: {
  hasPictures: boolean;
  expanded: boolean;
  isCaptionTruncated: boolean;
  onTextLayout: (event: TextLayoutEvent) => void;
  onToggle: () => void;
  readLessLabel: string;
  readMoreLabel: string;
  text: string;
}) {
  return (
    <Column gap="spacing.general.x1" style={[styles.captionBlock, !hasPictures && styles.textOnlyCaption]}>
      <Text accessible={false} onTextLayout={onTextLayout} style={[styles.captionText, styles.captionMeasurementText]}>
        {text}
      </Text>
      <Text numberOfLines={expanded ? undefined : POST_CAPTION_MAX_LINES} style={styles.captionText}>
        {text}
      </Text>
      {isCaptionTruncated ? (
        <Pressable accessibilityRole="button" hitSlop={8} onPress={onToggle}>
          <Text color="text.interactive" fontWeight="600" style={styles.readMoreText}>
            {expanded ? readLessLabel : readMoreLabel}
          </Text>
        </Pressable>
      ) : null}
    </Column>
  );
}

function PostActions({
  commentsCount,
  isLiked,
  isUpdatingLike,
  likeScaleStyle,
  likesCount,
  onOpenComments,
  onToggleLike,
}: {
  commentsCount: number;
  isLiked: boolean;
  isUpdatingLike: boolean;
  likeScaleStyle: ReturnType<typeof useAnimatedStyle>;
  likesCount: number;
  onOpenComments: () => void;
  onToggleLike: () => void;
}) {
  const { t } = useTranslation('posts');

  return (
    <Row alignItems="center" justifyContent="space-between" style={styles.actionBar}>
      <Row alignItems="center" gap="spacing.general.x4">
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`${t(isLiked ? 'feed.unlikeAction' : 'feed.likeAction')}, ${t('feed.likes', { count: likesCount })}`}
          accessibilityState={{ selected: isLiked, disabled: isUpdatingLike }}
          disabled={isUpdatingLike}
          onPress={onToggleLike}
          style={styles.actionButton}
        >
          <Reanimated.View style={likeScaleStyle}>
            {isLiked ? (
              <HeartFilledIcon color={theme.colors['icon.error']} size={22} />
            ) : (
              <HeartOutlineIcon color={theme.colors['text.primary']} size={22} />
            )}
          </Reanimated.View>
          {likesCount > 0 ? <Text style={[styles.actionCount, isLiked && styles.likedCount]}>{likesCount}</Text> : null}
        </Pressable>

        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`${t('comments.title')}, ${t('feed.comments', { count: commentsCount })}`}
          onPress={onOpenComments}
          style={styles.actionButton}
        >
          <CommentOutlineIcon color={theme.colors['text.primary']} size={22} />
          {commentsCount > 0 ? <Text style={styles.actionCount}>{commentsCount}</Text> : null}
        </Pressable>
      </Row>
    </Row>
  );
}

function PostCard({ post, showListingAvatar }: PostCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [isCaptionTruncated, setIsCaptionTruncated] = useState(false);
  const { t } = useTranslation('posts');
  const { showActionSheetWithOptions } = useActionSheet();
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [viewerImageIndex, setViewerImageIndex] = useState(0);
  const [visibleImageIndex, setVisibleImageIndex] = useState(0);
  const [mediaWidth, setMediaWidth] = useState(0);
  const navigation = useNavigation<SpecificNavigationProp>();
  const currentUser = useAtomValue(userInfoAtom);
  const isOwner = currentUser?.id === post.userId;
  const { isLargeUI } = useUISize();
  const { isLiked, isUpdatingLike, likeScaleStyle, likesCount, toggleLike } = usePostLike(post);

  const handleCaptionTextLayout = (event: TextLayoutEvent) => {
    const isTruncated = event.nativeEvent.lines.length > POST_CAPTION_MAX_LINES;
    setIsCaptionTruncated((wasTruncated) => {
      return wasTruncated === isTruncated ? wasTruncated : isTruncated;
    });
  };

  const details = getPostCardDetails(post, t('deletedUser', { ns: 'common' }));

  const handleImagePress = (index: number) => {
    setViewerImageIndex(index);
    setIsImageViewerOpen(true);
  };

  const handleMediaLayout = (event: { nativeEvent: { layout: { width: number } } }) => {
    const nextWidth = Math.round(event.nativeEvent.layout.width);
    setMediaWidth((current) => (current === nextWidth ? current : nextWidth));
  };

  const handleMediaScrollEnd = (event: { nativeEvent: { contentOffset: { x: number } } }) => {
    if (!mediaWidth) return;
    const nextIndex = Math.min(
      details.pictures.length - 1,
      Math.max(0, Math.round(event.nativeEvent.contentOffset.x / mediaWidth)),
    );
    setVisibleImageIndex(nextIndex);
  };

  const handleEdit = () => {
    navigation.navigate(
      'edit-post',
      {
        postId: post.id,
        listingId: post.loan.listing.id,
      },
      { pop: true },
    );
  };

  const handleOpenComments = () => {
    navigation.navigate('post-comments', { postId: post.id }, { pop: true });
  };

  const handleSharePost = async () => {
    if (isMobileNative) {
      await Share.open({
        title: `${details.authorName} · ${details.listingName}`,
        message: details.shareMessage,
        url: details.pictures[0]?.url ?? undefined,
      }).catch(() => ({ message: 'cancel' }));
      return;
    }

    await Clipboard.setStringAsync(details.shareMessage);
    Toast2.show(t('feed.shareCopied'), {
      duration: Toast2.durations.SHORT,
    });
  };

  const handleReportPost = () => {
    navigateToChat(navigation, SUPPORT_CHANNEL_URL, {
      i18nKey: 'feed.reportPostPrompt',
      ns: 'posts',
      values: { item: details.listingName, name: details.authorName },
    });
  };

  const handleOpenActionsMenu = () => {
    const options = isOwner
      ? [t('feed.shareAction'), t('feed.editAction'), t('feed.cancel')]
      : [t('feed.shareAction'), t('feed.reportAction'), t('feed.cancel')];

    const cancelButtonIndex = options.length - 1;

    showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
      },
      (selectedOption) => {
        if (selectedOption === cancelButtonIndex || selectedOption === undefined) return;

        if (selectedOption === 0) {
          void handleSharePost();
          return;
        }

        if (isOwner) {
          handleEdit();
          return;
        }

        handleReportPost();
      },
    );
  };

  const currentMediaWidth = mediaWidth || 320;
  const header = (
    <PostCardHeader
      authorName={details.authorName}
      overlay={details.pictures.length > 0}
      navigation={navigation}
      onOpenActionsMenu={handleOpenActionsMenu}
      post={post}
      timestamp={t('feed.timeAgo', {
        time: localeAwareFormatDistance(new Date(), new Date(post.createdAt)),
      })}
    />
  );

  const actions = (
    <PostActions
      commentsCount={details.commentsCount}
      isLiked={isLiked}
      isUpdatingLike={isUpdatingLike}
      likeScaleStyle={likeScaleStyle}
      likesCount={likesCount}
      onOpenComments={handleOpenComments}
      onToggleLike={toggleLike}
    />
  );
  const hasPictures = details.pictures.length > 0;

  return (
    <View style={styles.cardShadow}>
      <View style={styles.card}>
        <ImageViewerModal
          key={viewerImageIndex}
          imageUrls={details.imageUrls}
          index={viewerImageIndex}
          onClose={() => setIsImageViewerOpen(false)}
          open={isImageViewerOpen}
        />

        {details.pictures.length === 0 ? header : null}
        <PostMedia
          header={header}
          currentMediaWidth={currentMediaWidth}
          isLargeUI={isLargeUI}
          listingName={details.listingName}
          onImagePress={handleImagePress}
          onMediaLayout={handleMediaLayout}
          onMediaScrollEnd={handleMediaScrollEnd}
          pictures={details.pictures}
          visibleImageIndex={visibleImageIndex}
        />
        {hasPictures ? actions : null}
        <PostCaption
          hasPictures={hasPictures}
          expanded={expanded}
          isCaptionTruncated={isCaptionTruncated}
          onTextLayout={handleCaptionTextLayout}
          onToggle={() => setExpanded((current) => !current)}
          readLessLabel={t('feed.readLess')}
          readMoreLabel={t('feed.readMore')}
          text={post.text}
        />
        {!hasPictures ? actions : null}
        <PostListingContext
          lender={details.lender}
          lenderLabel={t('feed.lenderLabel')}
          lenderName={details.lenderName}
          listingId={details.listingId}
          listingLabel={t('feed.listingLabel')}
          listingName={details.listingName}
          listingPicture={details.listingPicture}
          listingType={details.listingType}
          navigation={navigation}
          showListingAvatar={showListingAvatar}
          usesRequestIcon={details.usesRequestIcon}
        />
      </View>
    </View>
  );
}

const FEED_EXIT_VIEWABILITY_CONFIG = { viewAreaCoveragePercentThreshold: 20 };
// Whole short footers qualify automatically; tall footers must fill most of the viewport.
const FEED_READY_VIEWABILITY_CONFIG = { viewAreaCoveragePercentThreshold: 70, minimumViewTime: 300 };
const FEED_FOOTER = { feedFooter: true, id: 'community-feed-completion' } as const;
type FeedRow = PostResponse | typeof FEED_FOOTER;
function isFeedFooter(item: FeedRow): item is typeof FEED_FOOTER {
  return item === FEED_FOOTER;
}

interface PostsListProps {
  posts: PostResponse[];
  /** False while a containing pager keeps this feed mounted offscreen. */
  isActive?: boolean;
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  /** Enable only when the social feed has successfully loaded its final page. */
  isEndOfFeed?: boolean;
  onEndReached?: () => void;
  /**
   * When true, displays the related listing thumbnail and name on the post.
   * Use on the user feed (where posts are detached from listing context).
   * Defaults to false so listing screens don't repeat their own thumbnail.
   */
  showListingAvatar?: boolean;
  onScroll?: ScrollHandlerProcessed | ((event: NativeSyntheticEvent<NativeScrollEvent>) => void) | undefined;
  contentContainerStyle?: StyleProp<ViewStyle>;
  ListHeaderComponent?: React.ComponentProps<typeof FlatList>['ListHeaderComponent'];
}

export function PostsList({
  posts,
  isActive = true,
  isLoading,
  isFetchingNextPage,
  isEndOfFeed = false,
  onEndReached,
  showListingAvatar = false,
  onScroll,
  contentContainerStyle,
  ListHeaderComponent,
}: PostsListProps) {
  const { t } = useTranslation('posts');
  const focused = useIsFocused();
  const [appState, setAppState] = useState(AppState.currentState);
  const [footerInViewport, setFooterInViewport] = useState(false);
  const onFooterExit = useCallback(({ viewableItems }: { viewableItems: ViewToken<FeedRow>[] }) => {
    if (!viewableItems.some((token) => token.isViewable && isFeedFooter(token.item))) setFooterInViewport(false);
  }, []);
  const onFooterReady = useCallback(({ viewableItems }: { viewableItems: ViewToken<FeedRow>[] }) => {
    if (viewableItems.some((token) => token.isViewable && isFeedFooter(token.item))) setFooterInViewport(true);
  }, []);
  const viewabilityConfigCallbackPairs = useMemo(
    () => [
      { viewabilityConfig: FEED_EXIT_VIEWABILITY_CONFIG, onViewableItemsChanged: onFooterExit },
      { viewabilityConfig: FEED_READY_VIEWABILITY_CONFIG, onViewableItemsChanged: onFooterReady },
    ],
    [onFooterExit, onFooterReady],
  );
  useEffect(() => {
    const subscription = AppState.addEventListener('change', setAppState);
    return () => subscription.remove();
  }, []);
  const hasFooter = isEndOfFeed && !isLoading && !isFetchingNextPage && posts.length > 0;
  const rows = useMemo<FeedRow[]>(() => (hasFooter ? [...posts, FEED_FOOTER] : posts), [hasFooter, posts]);
  const footerVisible = footerInViewport && isActive && focused && appState === 'active';
  const renderPost = ({ item }: { item: FeedRow }) =>
    isFeedFooter(item) ? (
      <PostsFeedFooter animate={footerVisible} />
    ) : (
      <PostCard post={item} showListingAvatar={showListingAvatar} />
    );

  if (isLoading && !ListHeaderComponent) {
    return (
      <Column alignItems="center" flex={1} justifyContent="center" padding="spacing.general.x12">
        <Text color="text.tertiary">{t('feed.loading')}</Text>
      </Column>
    );
  }

  return (
    <Reanimated.FlatList
      contentContainerStyle={[
        contentContainerStyle,
        !contentContainerStyle
          ? {
              paddingTop: theme.spacing['spacing.general.x2'],
              paddingBottom: theme.spacing['spacing.general.x36'],
            }
          : {},
      ]}
      data={rows}
      extraData={footerVisible}
      viewabilityConfigCallbackPairs={viewabilityConfigCallbackPairs}
      ListHeaderComponent={ListHeaderComponent}
      onScroll={onScroll}
      ItemSeparatorComponent={ItemSeparator}
      keyExtractor={(item) => item.id}
      ListEmptyComponent={
        isLoading ? (
          <Column alignItems="center" flex={1} justifyContent="center" padding="spacing.general.x12">
            <Text color="text.tertiary">{t('feed.loading')}</Text>
          </Column>
        ) : (
          <Column alignItems="center" gap="spacing.general.x2" padding="spacing.general.x12">
            <Text color="text.primary" fontWeight="700" textAlign="center" variant="bodyLarge">
              {t('feed.emptyTitle')}
            </Text>
            <Text color="text.secondary" textAlign="center" variant="bodyBase">
              {t('feed.emptyText')}
            </Text>
          </Column>
        )
      }
      ListFooterComponent={
        isFetchingNextPage ? (
          <Column alignItems="center" padding="spacing.general.x4">
            <Text color="text.tertiary">{t('feed.loading')}</Text>
          </Column>
        ) : null
      }
      onEndReached={onEndReached}
      onEndReachedThreshold={0.4}
      renderItem={renderPost}
      showsVerticalScrollIndicator={false}
      style={styles.feedList}
    />
  );
}

const styles = StyleSheet.create({
  feedList: {
    backgroundColor: theme.colors['surface.primary-neutral'],
  },
  cardShadow: {
    backgroundColor: theme.colors['surface.default'],
  },
  card: {
    backgroundColor: theme.colors['surface.default'],
    paddingBottom: theme.spacing['spacing.general.x2'],
    overflow: 'hidden',
    borderBottomColor: theme.colors['border.secondary'],
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  textOnlyHeader: {
    paddingBottom: theme.spacing['spacing.general.x2'],
  },
  textOnlyCaption: {
    paddingTop: 0,
  },
  header: {
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    paddingTop: theme.spacing['spacing.general.x4'],
    paddingBottom: theme.spacing['spacing.general.x4'],
  },
  mediaHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
  headerGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 220,
  },
  authorAvatarRing: {
    padding: 2,
    borderRadius: theme.borderRadii['radius.full'],
    backgroundColor: 'rgba(255,255,255,0.85)',
  },
  overlayAuthorName: {
    color: theme.colors['icon.inverse.primary'],
  },
  overlayTimestamp: {
    color: 'rgba(255,255,255,0.85)',
  },
  overlayMenuButton: {
    backgroundColor: 'rgba(0,0,0,0.18)',
    borderRadius: theme.borderRadii['radius.full'],
  },
  authorName: {
    color: theme.colors['text.primary'],
    fontSize: 15,
    lineHeight: 20,
  },
  timestamp: {
    color: theme.colors['text.secondary'],
    fontSize: 12,
    lineHeight: 20,
  },
  listingContext: {
    marginTop: theme.spacing['spacing.general.x2'],
    marginHorizontal: theme.spacing['spacing.general.x4'],
  },
  contextCopy: {
    flex: 1,
    minWidth: 0,
  },
  contextLabel: {
    color: theme.colors['text.secondary'],
    fontSize: 10,
    lineHeight: 13,
  },
  contextNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  listingContextImage: {
    height: '100%',
    width: '100%',
  },
  listingContextThumbnail: {
    borderRadius: theme.borderRadii['radius.full'],
    height: 24,
    overflow: 'hidden',
    width: 24,
  },
  listingContextItem: {
    alignItems: 'center',
    flex: 1.3,
    minHeight: 36,
    flexDirection: 'row',
    gap: theme.spacing['spacing.general.x2'],
    minWidth: 0,
  },
  lenderContextItem: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.spacing['spacing.general.x2'],
    minWidth: 0,
    minHeight: 36,
  },
  contextPressed: {
    opacity: 0.55,
  },
  lenderContextText: {
    textDecorationLine: 'underline',
    textDecorationColor: theme.colors['border.secondary'],
    color: theme.colors['text.primary'],
    flexShrink: 1,
    fontSize: 12,
    lineHeight: 16,
  },
  listingContextText: {
    textDecorationLine: 'underline',
    textDecorationColor: theme.colors['border.secondary'],
    color: theme.colors['text.primary'],
    flexShrink: 1,
    fontSize: 12,
    lineHeight: 16,
  },
  menuButton: {
    alignItems: 'center',
    borderRadius: 999,
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  mediaSection: {
    overflow: 'hidden',
  },
  mediaFrameLarge: {
    aspectRatio: 16 / 9,
  },
  mediaFrame: {
    aspectRatio: 4 / 5,
    backgroundColor: '#F5F4F1',
  },
  mediaImage: {
    height: '100%',
    width: '100%',
  },
  mediaFallbackBackground: {
    ...StyleSheet.absoluteFill,
    alignItems: 'center',
    backgroundColor: theme.colors['surface.brand.default'],
    justifyContent: 'center',
    padding: theme.spacing['spacing.general.x4'],
  },
  mediaFallbackTitle: {
    fontSize: 18,
    lineHeight: 24,
    textAlign: 'center',
  },
  carouselPosition: {
    position: 'absolute',
    bottom: 12,
    alignSelf: 'center',
    borderRadius: 20,
    backgroundColor: 'rgba(0,0,0,0.3)',
    paddingHorizontal: 10,
    paddingVertical: 7,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['spacing.general.x2'],
  },
  pagination: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing['spacing.general.x1'],
  },
  paginationDot: {
    backgroundColor: 'rgba(255,255,255,0.5)',
    borderRadius: theme.borderRadii['radius.full'],
    height: 5,
    width: 5,
  },
  paginationDotActive: {
    backgroundColor: '#fff',
    width: 14,
  },
  captionBlock: {
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    paddingTop: theme.spacing['spacing.general.x1'],
  },
  captionText: {
    color: theme.colors['text.primary'],
    fontSize: 15,
    lineHeight: 22,
  },
  captionMeasurementText: {
    left: theme.spacing['spacing.general.x4'],
    opacity: 0,
    position: 'absolute',
    right: theme.spacing['spacing.general.x4'],
  },
  readMoreText: {
    fontSize: 14,
    lineHeight: 18,
  },
  actionBar: {
    paddingBottom: 0,
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    paddingTop: theme.spacing['spacing.general.x1'],
  },
  actionButton: {
    minHeight: 44,
    minWidth: 44,
    alignItems: 'center',
    flexDirection: 'row',
    gap: 6,
  },
  likedCount: {
    color: theme.colors['icon.error'],
  },
  actionCount: {
    color: theme.colors['text.primary'],
    fontSize: 13,
    fontWeight: '600',
    lineHeight: 16,
  },
});
